import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { View, LayoutChangeEvent, Platform } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { Gesture, GestureDetector, GestureHandlerRootView } from 'react-native-gesture-handler';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';

export interface IRangeSliderProps {
  min: number;
  max: number;
  value: [number, number];
  onValueChange: (value: [number, number]) => void;
  onDraggingChange?: (isDragging: boolean) => void;
  minimumDistancePercent?: number;
  step?: number;
  formatValue?: (value: number) => string;
  formatRangeDisplay?: (minValue: number, maxValue: number, formatValue: (v: number) => string) => string | React.ReactNode;
  valueDisplayMode?: 'ends' | 'right';
  label?: string;
  labelAccessory?: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

const THUMB_SIZE = 24;
const TRACK_HEIGHT = 6;
const DEFAULT_MINIMUM_DISTANCE_PERCENT = 0; // Allow exact ranges where both thumbs use the same value
const UPDATE_THROTTLE_MS = 100; // Throttle updates during drag
const THUMB_TOUCH_PADDING = 14; // Increase touch target without changing visual size
const THUMB_TOUCH_SIZE = THUMB_SIZE + THUMB_TOUCH_PADDING * 2;
const MIN_THUMB_INDEX = 0;
const MAX_THUMB_INDEX = 1;

export function RangeSlider({
  min,
  max,
  value,
  onValueChange,
  onDraggingChange,
  minimumDistancePercent = DEFAULT_MINIMUM_DISTANCE_PERCENT,
  step = 1,
  formatValue = (v) => String(v),
  formatRangeDisplay,
  valueDisplayMode = 'ends',
  label,
  labelAccessory,
  className,
  disabled = false,
}: IRangeSliderProps) {
  const isWeb = Platform.OS === 'web';
  const trackWidth = useSharedValue(0);
  const minPosition = useSharedValue(0);
  const maxPosition = useSharedValue(0);
  const isMinDragging = useSharedValue(false);
  const isMaxDragging = useSharedValue(false);
  // Store the starting position when gesture begins
  const minStartPosition = useSharedValue(0);
  const maxStartPosition = useSharedValue(0);
  // On web, keep the thumb centered under the mouse pointer even if drag starts off-center.
  // (Native typically feels fine with relative dragging.)
  const minGrabOffset = useSharedValue(0);
  const maxGrabOffset = useSharedValue(0);
  // Track last update time for throttling
  const lastUpdateTime = useSharedValue(0);
  // Thumb scale driven by gesture lifecycle (avoids withSpring inside useAnimatedStyle)
  const minThumbScale = useSharedValue(1);
  const maxThumbScale = useSharedValue(1);
  const lastInteractedThumb = useSharedValue(MAX_THUMB_INDEX);
  const minimumDistancePercentShared = useSharedValue(Math.max(0, Math.min(1, minimumDistancePercent)));

  useEffect(() => {
    minimumDistancePercentShared.value = Math.max(0, Math.min(1, minimumDistancePercent));
  }, [minimumDistancePercent, minimumDistancePercentShared]);

  const handleDraggingChange = useCallback(
    (isDragging: boolean) => {
      onDraggingChange?.(isDragging);
    },
    [onDraggingChange]
  );

  const valueToPosition = useCallback(
    (val: number, width: number) => {
      if (width <= 0) return 0;
      return ((val - min) / (max - min)) * width;
    },
    [min, max]
  );

  const getDefaultThumbPriority = useCallback((minPos: number, maxPos: number, width: number) => {
    const midpoint = width / 2;

    if (maxPos <= midpoint) {
      return MAX_THUMB_INDEX;
    }

    if (minPos >= midpoint) {
      return MIN_THUMB_INDEX;
    }

    return MAX_THUMB_INDEX;
  }, []);

  const getMinimumValueDistance = useCallback(() => {
    const valueRange = max - min;
    const normalizedMinimumDistancePercent = Math.max(0, Math.min(1, minimumDistancePercent));

    if (valueRange <= 0 || normalizedMinimumDistancePercent <= 0) {
      return 0;
    }

    const rawDistance = valueRange * normalizedMinimumDistancePercent;
    const steppedDistance = step > 0 ? Math.ceil(rawDistance / step) * step : rawDistance;

    return Math.min(valueRange, steppedDistance);
  }, [max, min, minimumDistancePercent, step]);

  const enforceMinimumValueDistance = useCallback(
    (
      nextMin: number,
      nextMax: number,
      activeThumbIndex?: typeof MIN_THUMB_INDEX | typeof MAX_THUMB_INDEX
    ): [number, number] => {
      const minimumDistance = getMinimumValueDistance();
      const orderedMin = Math.min(nextMin, nextMax);
      const orderedMax = Math.max(nextMin, nextMax);

      if (minimumDistance <= 0 || orderedMax - orderedMin >= minimumDistance) {
        return [orderedMin, orderedMax];
      }

      if (minimumDistance >= max - min) {
        return [min, max];
      }

      if (activeThumbIndex === MIN_THUMB_INDEX) {
        const adjustedMin = Math.max(min, orderedMax - minimumDistance);
        return [adjustedMin, adjustedMin + minimumDistance];
      }

      if (activeThumbIndex === MAX_THUMB_INDEX) {
        const adjustedMax = Math.min(max, orderedMin + minimumDistance);
        return [adjustedMax - minimumDistance, adjustedMax];
      }

      const expandedMax = Math.min(max, orderedMin + minimumDistance);

      if (expandedMax - orderedMin >= minimumDistance) {
        return [orderedMin, expandedMax];
      }

      return [max - minimumDistance, max];
    },
    [getMinimumValueDistance, max, min]
  );

  // JS function to calculate values and call onValueChange
  const calculateAndUpdateValues = useCallback(
    (
      minPos: number,
      maxPos: number,
      width: number,
      activeThumbIndex?: typeof MIN_THUMB_INDEX | typeof MAX_THUMB_INDEX
    ) => {
      if (width <= 0) return;
      
      // Convert positions to values
      const rawMinValue = (minPos / width) * (max - min) + min;
      const rawMaxValue = (maxPos / width) * (max - min) + min;
      
      // Apply stepping
      const steppedMin = Math.round(rawMinValue / step) * step;
      const steppedMax = Math.round(rawMaxValue / step) * step;
      
      // Clamp values
      const clampedMin = Math.max(min, Math.min(max, steppedMin));
      const clampedMax = Math.max(min, Math.min(max, steppedMax));
      
      const [finalMin, finalMax] = enforceMinimumValueDistance(clampedMin, clampedMax, activeThumbIndex);
      
      onValueChange([finalMin, finalMax]);
    },
    [enforceMinimumValueDistance, min, max, step, onValueChange]
  );

  useEffect(() => {
    const [nextMin, nextMax] = enforceMinimumValueDistance(value[0], value[1]);
    if (nextMin !== value[0] || nextMax !== value[1]) {
      onValueChange([nextMin, nextMax]);
    }
  }, [enforceMinimumValueDistance, onValueChange, value]);

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const width = event.nativeEvent.layout.width - THUMB_SIZE;
      const nextMinPosition = valueToPosition(value[0], width);
      const nextMaxPosition = valueToPosition(value[1], width);
      trackWidth.value = width;
      minPosition.value = nextMinPosition;
      maxPosition.value = nextMaxPosition;
      lastInteractedThumb.value = getDefaultThumbPriority(nextMinPosition, nextMaxPosition, width);
    },
    [getDefaultThumbPriority, value, valueToPosition]
  );

  // Update positions when value prop changes (e.g., programmatic reset)
  useEffect(() => {
    if (trackWidth.value > 0 && !isMinDragging.value && !isMaxDragging.value) {
      minPosition.value = withSpring(valueToPosition(value[0], trackWidth.value), { damping: 26, stiffness: 200 });
      maxPosition.value = withSpring(valueToPosition(value[1], trackWidth.value), { damping: 26, stiffness: 200 });
    }
  }, [value, valueToPosition, trackWidth, minPosition, maxPosition, isMinDragging, isMaxDragging]);

  // Stable refs for callbacks used in gesture worklets
  const handleDraggingChangeRef = useRef(handleDraggingChange);
  const calculateAndUpdateValuesRef = useRef(calculateAndUpdateValues);
  handleDraggingChangeRef.current = handleDraggingChange;
  calculateAndUpdateValuesRef.current = calculateAndUpdateValues;

  const disabledShared = useSharedValue(disabled);
  useEffect(() => { disabledShared.value = disabled; }, [disabled]);

  const callDraggingChange = (isDragging: boolean) => handleDraggingChangeRef.current(isDragging);
  const callCalcUpdate = (
    minPos: number,
    maxPos: number,
    width: number,
    activeThumbIndex?: typeof MIN_THUMB_INDEX | typeof MAX_THUMB_INDEX
  ) => calculateAndUpdateValuesRef.current(minPos, maxPos, width, activeThumbIndex);

  const minGesture = useMemo(() => Gesture.Pan()
    // Android: if the user's first movement has too much Y, the parent ScrollView can "win" and
    // begin scrolling before this Pan gesture activates. We lock the parent scroll on touch-down
    // (minDistance(0) + onBegin) so the slider keeps control of the interaction.
    .minDistance(0)
    .onBegin(() => {
      'worklet';
      if (disabledShared.value) return;
      lastInteractedThumb.value = MIN_THUMB_INDEX;
      minThumbScale.value = withSpring(1.2, { damping: 20, stiffness: 200 });
      scheduleOnRN(callDraggingChange, true);
    })
    .onStart((e) => {
      'worklet';
      if (disabledShared.value) return;
      isMinDragging.value = true;
      minStartPosition.value = minPosition.value;
      minGrabOffset.value = isWeb ? e.x - THUMB_TOUCH_PADDING - THUMB_SIZE / 2 : 0;
      lastUpdateTime.value = Date.now();
    })
    .onUpdate((e) => {
      'worklet';
      if (disabledShared.value) return;
      const minGap = trackWidth.value * minimumDistancePercentShared.value;
      const maxAllowed = maxPosition.value - minGap;
      const newPos = minStartPosition.value + e.translationX + minGrabOffset.value;
      minPosition.value = Math.max(0, Math.min(newPos, maxAllowed));
      
      const now = Date.now();
      if (now - lastUpdateTime.value >= UPDATE_THROTTLE_MS) {
        lastUpdateTime.value = now;
        scheduleOnRN(callCalcUpdate, minPosition.value, maxPosition.value, trackWidth.value, MIN_THUMB_INDEX);
      }
    })
    .onEnd(() => {
      'worklet';
      if (disabledShared.value) return;
      scheduleOnRN(callCalcUpdate, minPosition.value, maxPosition.value, trackWidth.value, MIN_THUMB_INDEX);
    })
    .onFinalize(() => {
      'worklet';
      isMinDragging.value = false;
      minThumbScale.value = withSpring(1, { damping: 20, stiffness: 200 });
      scheduleOnRN(callDraggingChange, false);
    }), []);

  const maxGesture = useMemo(() => Gesture.Pan()
    // Same scroll-lock behavior as the min thumb (see comment above).
    .minDistance(0)
    .onBegin(() => {
      'worklet';
      if (disabledShared.value) return;
      lastInteractedThumb.value = MAX_THUMB_INDEX;
      maxThumbScale.value = withSpring(1.2, { damping: 20, stiffness: 200 });
      scheduleOnRN(callDraggingChange, true);
    })
    .onStart((e) => {
      'worklet';
      if (disabledShared.value) return;
      isMaxDragging.value = true;
      maxStartPosition.value = maxPosition.value;
      maxGrabOffset.value = isWeb ? e.x - THUMB_TOUCH_PADDING - THUMB_SIZE / 2 : 0;
      lastUpdateTime.value = Date.now();
    })
    .onUpdate((e) => {
      'worklet';
      if (disabledShared.value) return;
      const minGap = trackWidth.value * minimumDistancePercentShared.value;
      const minAllowed = minPosition.value + minGap;
      const newPos = maxStartPosition.value + e.translationX + maxGrabOffset.value;
      maxPosition.value = Math.max(minAllowed, Math.min(newPos, trackWidth.value));
      
      const now = Date.now();
      if (now - lastUpdateTime.value >= UPDATE_THROTTLE_MS) {
        lastUpdateTime.value = now;
        scheduleOnRN(callCalcUpdate, minPosition.value, maxPosition.value, trackWidth.value, MAX_THUMB_INDEX);
      }
    })
    .onEnd(() => {
      'worklet';
      if (disabledShared.value) return;
      scheduleOnRN(callCalcUpdate, minPosition.value, maxPosition.value, trackWidth.value, MAX_THUMB_INDEX);
    })
    .onFinalize(() => {
      'worklet';
      isMaxDragging.value = false;
      maxThumbScale.value = withSpring(1, { damping: 20, stiffness: 200 });
      scheduleOnRN(callDraggingChange, false);
    }), []);

  const minThumbStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: minPosition.value - THUMB_TOUCH_PADDING },
      { translateY: -THUMB_TOUCH_SIZE / 2 },
    ],
    zIndex: lastInteractedThumb.value === MIN_THUMB_INDEX ? 2 : 1,
    elevation: lastInteractedThumb.value === MIN_THUMB_INDEX ? 2 : 1,
  }));

  const maxThumbStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: maxPosition.value - THUMB_TOUCH_PADDING },
      { translateY: -THUMB_TOUCH_SIZE / 2 },
    ],
    zIndex: lastInteractedThumb.value === MAX_THUMB_INDEX ? 2 : 1,
    elevation: lastInteractedThumb.value === MAX_THUMB_INDEX ? 2 : 1,
  }));

  const filledTrackStyle = useAnimatedStyle(() => ({
    left: minPosition.value + THUMB_SIZE / 2,
    width: Math.max(0, maxPosition.value - minPosition.value),
  }));

  const minThumbActiveStyle = useAnimatedStyle(() => ({
    transform: [{ scale: minThumbScale.value }],
  }));

  const maxThumbActiveStyle = useAnimatedStyle(() => ({
    transform: [{ scale: maxThumbScale.value }],
  }));

  const rangeDisplay = formatRangeDisplay 
    ? formatRangeDisplay(value[0], value[1], formatValue)
    : `${formatValue(value[0])} - ${formatValue(value[1])}`;

  const sliderContent = (
    <View className={`${className ?? ''}`}>
      {valueDisplayMode === 'right' ? (
        label ? (
          <HStack className="mb-2 items-center justify-between">
            <HStack className="items-center gap-2">
              <Text size="md" className="font-semibold text-typography-700">
                {label}
              </Text>
              {labelAccessory ? labelAccessory : null}
            </HStack>
            {typeof rangeDisplay === 'string' ? (
              <Text size="sm" className="font-medium text-typography-700">
                {rangeDisplay}
              </Text>
            ) : (
              rangeDisplay
            )}
          </HStack>
        ) : (
          <Text size="sm" className="mb-2 text-center font-medium text-typography-700">
            {rangeDisplay}
          </Text>
        )
      ) : (
        <HStack className="mb-2 justify-between">
          <Text size="sm" className="font-medium text-typography-700">
            {formatValue(value[0])}
          </Text>
          <Text size="sm" className="font-medium text-typography-700">
            {formatValue(value[1])}
          </Text>
        </HStack>
      )}
      <View
        className="relative h-8 justify-center"
        onLayout={handleLayout}
        style={{ opacity: disabled ? 0.5 : 1 }}>
        {/* Background Track */}
        <View
          className="absolute left-3 right-3 rounded-full bg-background-200"
          style={{
            height: TRACK_HEIGHT,
            top: '50%',
            transform: [{ translateY: -TRACK_HEIGHT / 2 }],
          }}
        />
        {/* Filled Track */}
        <Animated.View
          style={[
            {
              position: 'absolute',
              height: TRACK_HEIGHT,
              top: '50%',
              transform: [{ translateY: -TRACK_HEIGHT / 2 }],
            },
            filledTrackStyle,
          ]}>
          <View className="h-full w-full rounded-full bg-primary-500" />
        </Animated.View>
        {/* Min Thumb */}
        <GestureDetector gesture={minGesture}>
          <Animated.View
            style={[
              {
                position: 'absolute',
                width: THUMB_TOUCH_SIZE,
                height: THUMB_TOUCH_SIZE,
                top: '50%',
                justifyContent: 'center',
                alignItems: 'center',
              },
              minThumbStyle,
            ]}>
            <Animated.View
              style={[
                {
                  width: THUMB_SIZE,
                  height: THUMB_SIZE,
                },
                minThumbActiveStyle,
              ]}>
              <View className="h-full w-full rounded-full bg-primary-500 shadow-md" />
            </Animated.View>
          </Animated.View>
        </GestureDetector>
        {/* Max Thumb */}
        <GestureDetector gesture={maxGesture}>
          <Animated.View
            style={[
              {
                position: 'absolute',
                width: THUMB_TOUCH_SIZE,
                height: THUMB_TOUCH_SIZE,
                top: '50%',
                justifyContent: 'center',
                alignItems: 'center',
              },
              maxThumbStyle,
            ]}>
            <Animated.View
              style={[
                {
                  width: THUMB_SIZE,
                  height: THUMB_SIZE,
                },
                maxThumbActiveStyle,
              ]}>
              <View className="h-full w-full rounded-full bg-primary-500 shadow-md" />
            </Animated.View>
          </Animated.View>
        </GestureDetector>
      </View>
    </View>
  );

  // Wrap with GestureHandlerRootView on native to avoid the warning
  if (Platform.OS !== 'web') {
    return (
      <GestureHandlerRootView style={{ flex: 0 }}>
        {sliderContent}
      </GestureHandlerRootView>
    );
  }

  return sliderContent;
}
